import { EVIDENCE_KINDS } from "./projectionContract";

// ENG-009 — Unknown & Recovery. Mirrors the established
// authorizationComposition.js / controllerFenceComposition.js pattern:
// deterministic identity, an in-memory idempotent registry, and a pure
// resolution function driven only by already-composed fake evidence.
// Reconciliation request, reconciliation execution, repair, and owner
// resolution remain distinct (ENG-009 §4) — this module captures and
// resolves requests; it never executes reconciliation, repair, or any
// owner mutation, and contains no network, storage, or provider reference.

export const RECONCILIATION_REQUEST_STATUSES = Object.freeze({
  CAPTURED: "captured",
  RESOLVED: "resolved",
  UNRESOLVED: "unresolved",
});

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
}

/**
 * Deterministic reconciliation-request identity from stable logical inputs
 * only (mirrors controllerFenceComposition.js's computeAttemptIdentity).
 * Retrying the same logical request — same Account, subject owner, subject
 * projection, and subject revision — always yields the same identity, so
 * repeated equivalent requests converge on one captured record instead of
 * minting duplicates. Random values and wall clock are deliberately
 * excluded so retry or re-render never changes identity.
 */
export function computeReconciliationRequestIdentity({
  accountId,
  subjectOwner,
  subjectProjectionId,
  subjectRevision,
}) {
  if (!accountId || !subjectOwner || !subjectProjectionId || !Number.isInteger(subjectRevision)) {
    throw new Error(
      "computeReconciliationRequestIdentity requires accountId, subjectOwner, subjectProjectionId, and subjectRevision"
    );
  }
  return [
    "fake-reconciliation-request",
    accountId,
    subjectOwner,
    subjectProjectionId,
    subjectRevision,
  ].join("::");
}

/**
 * In-memory, resettable registry of captured reconciliation requests.
 * Capturing performs no domain effect, network call, queue write, or
 * reconciliation execution — it only records that a request was made, so a
 * repeated capture of the same logical request converges on the previously
 * captured record instead of being treated as new (ENG-009 §6
 * "Reconciliation requests are idempotent and effect-free").
 */
export function createReconciliationRequestRegistry() {
  const requests = new Map();

  function getRequest(requestId) {
    return requests.get(requestId) ?? null;
  }

  function captureRequest(requestId, request) {
    const existing = requests.get(requestId);
    if (existing) return existing;
    const frozen = deepFreeze({ ...request, requestId });
    requests.set(requestId, frozen);
    return frozen;
  }

  function reset() {
    requests.clear();
  }

  return Object.freeze({ getRequest, captureRequest, reset });
}

/**
 * Captures one reconciliation request for the given indeterminate subject,
 * converging duplicate captures of the same logical request onto the same
 * record. Never executes reconciliation, repair, or any owner mutation.
 */
export function captureReconciliationRequest({
  accountId,
  subjectOwner,
  subjectProjectionId,
  subjectRevision,
  registry,
  capturedAt,
}) {
  const requestId = computeReconciliationRequestIdentity({
    accountId,
    subjectOwner,
    subjectProjectionId,
    subjectRevision,
  });
  return registry.captureRequest(requestId, {
    accountId,
    subjectOwner,
    subjectProjectionId,
    subjectRevision,
    capturedAt,
    status: RECONCILIATION_REQUEST_STATUSES.CAPTURED,
  });
}

/**
 * Deterministic fake authoritative resolution for a captured reconciliation
 * request, driven only by the subject's own already-composed evidence.kind
 * — never by live provider truth, which does not exist in Boundary A. A
 * RECONCILIATION_REQUIRED subject represents an owner that has already
 * signaled it can resolve the ambiguity; a plain UNKNOWN subject represents
 * an owner with no resolving evidence yet, so Unknown is preserved rather
 * than optimistically resolved (ENG-009 §4 "Unknown is preserved until
 * authoritative fake evidence resolves it"). Pure — never mutates the
 * registry's captured record; capture and resolution remain distinct.
 */
export function resolveReconciliationOutcome({ request, subjectEvidenceKind, resolvedAt }) {
  if (!request) {
    throw new Error("resolveReconciliationOutcome requires a captured request");
  }
  if (subjectEvidenceKind === EVIDENCE_KINDS.RECONCILIATION_REQUIRED) {
    return deepFreeze({
      ...request,
      status: RECONCILIATION_REQUEST_STATUSES.RESOLVED,
      resolvedAt,
      resolution: {
        kind: "fake",
        outcome: "owner-confirmed",
        source: "missionassist-boundary-a-static-fixture",
      },
    });
  }
  return deepFreeze({
    ...request,
    status: RECONCILIATION_REQUEST_STATUSES.UNRESOLVED,
    resolvedAt: null,
    resolution: null,
  });
}
